import { LlmHistoryNode } from '@adaptiva/ai/common';
import { BaseThemeService } from '@adaptiva/core/themes';
import { AlertComponentModule } from '@adaptiva/widgets/alert';
import { SearchNavigationComponent } from '@adaptiva/widgets/search-navigation';
import { IGridStyle, ViewboxComponent, ViewboxControlComponent, viewboxDotGrid } from '@adaptiva/widgets/viewbox';
import { KeyValuePipe, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal, Signal, viewChildren, WritableSignal } from '@angular/core';
import { BaseLlmTabComponent } from '../base-llm-tab';
import { LlmHistoryNodeConnectorComponent } from './connector';
import { LlmHistoryNodeComponent } from './history-node';
import { LlmHistoryComponentService } from './llm-history-component.service';
import { IsInLlmForkPipe, IsLlmNodeExpandedPipe, LlmHistoryNodeBoxPipe, LlmHistoryNodeColorPipe } from './pipes';

@Component({
	selector: 'ada-llm-history',
	templateUrl: './llm-history.component.html',
	styleUrl: './llm-history.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AlertComponentModule,
		IsInLlmForkPipe,
		IsLlmNodeExpandedPipe,
		KeyValuePipe,
		LlmHistoryNodeBoxPipe,
		LlmHistoryNodeColorPipe,
		LlmHistoryNodeComponent,
		LlmHistoryNodeConnectorComponent,
		NgStyle,
		SearchNavigationComponent,
		ViewboxComponent,
		ViewboxControlComponent,
	],
	providers: [LlmHistoryComponentService],
	host: {
		'[class.ada-display-block]': 'true',
		'[class.ada-full-size]': 'true',
	},
})
export class LlmHistoryComponent extends BaseLlmTabComponent {

	protected readonly service: LlmHistoryComponentService = inject(LlmHistoryComponentService);

	protected readonly searchResults: WritableSignal<LlmHistoryNode[]> = signal([]);

	protected readonly searchIndex: WritableSignal<int> = linkedSignal(() => {
		return !this.searchResults()?.length ? null : 0;
	});

	protected readonly themes: BaseThemeService = inject(BaseThemeService);

	protected readonly nodeComponents: Signal<readonly LlmHistoryNodeComponent[]> = viewChildren(LlmHistoryNodeComponent);

	protected readonly nodeComponentMap: Signal<WeakMap<LlmHistoryNode, LlmHistoryNodeComponent>> = computed(() => {
		this.service.history.viewbox.redrawTick();

		return this.nodeComponents().reduce(
			(_map: WeakMap<LlmHistoryNode, LlmHistoryNodeComponent>, _component: LlmHistoryNodeComponent) => {
				const _node: LlmHistoryNode | undefined = _component.node();

				if (_node)
					return _map.set(_node, _component);

				return _map;
			},
			new WeakMap()
		);
	});

	public readonly backgroundGridStyles: Signal<IGridStyle[]> = computed(() => {
		return viewboxDotGrid({
			backgroundColor: this.themes.getColor('background').darken(5),
			dotRadius: .5,
			gridColor: 'foreground'
		});
	});

	protected onNodeToggled(_node: LlmHistoryNode, _isExpanded: boolean): void {
		if (_isExpanded)
			this.service.expand(_node);
		else
			this.service.collapse(_node);

		this.service.history.viewbox.redraw();
	}

	protected search(_query: string): void {
		const _searchQuery: string = _query.toLowerCase();
		const _nodes: LlmHistoryNode[] = Array.from(this.service.displayNodes().values());
		const _filtered: LlmHistoryNode[] = _nodes.filter((_value: LlmHistoryNode) =>
			_value.userMessage.toLowerCase().includes(_searchQuery) ||
			_value.llmResponse.toLowerCase().includes(_searchQuery)
		).sort();

		this.searchResults.set(_filtered);
		this.service.history.setViewboxTarget(_filtered[this.searchIndex()]?.nodeId || null);
	}

	protected indexChange(_newIndex: int): void {
		if (this.searchResults().length)
			this.service.history.setViewboxTarget(this.searchResults()[_newIndex].nodeId);
	}

}
